import { Controller } from '@hotwired/stimulus';
import {
  hide as hideArticleActions,
  show as showArticleActions,
} from '@controllers/main/article-actions_controller';
import CurrentPage from '@frontend/app/features/CurrentPage';
import { dispatchCustomEvent } from '@frontend/utils/events';

export default class extends Controller {
  static targets = ['articleHeader', 'content', 'topbar'];
  static values = {
    navbarIsStickyToTop: { type: Boolean, default: false },
    previousScrollPosition: { type: Number, default: 0 },
    wasScrollingDown: { type: Boolean, default: false },
    topbarHidden: { type: Boolean, default: false },
    articleActionsHidden: { type: Boolean, default: true },
    forceNavbarDefaultColors: { type: Boolean, default: false },
  };

  connect() {
    if (!this.hasContentTarget || !this.hasTopbarTarget) return false;

    this.navbarPositionIsSticky = window.getComputedStyle(this.topbarTarget).position === "sticky";

    if (CurrentPage.isArticlePreview()) {
      this.setupArticlePreview();
    } else {
      this.setup();
    }
  }

  setupArticlePreview() {
    hideArticleActions();
    this.hideTopbar();
  }

  setup() {
    // NB: I tried to optimize this with the debounce function but as a result
    // the hide/show logic triggered too delayed on touch devices. I didn't take
    // the time to further investigate this.
    window.addEventListener('scroll', this.handleScrollChange.bind(this));
    document.body.addEventListener('article-scroll:article-actions-loaded', this.handleArticleActionsLoaded.bind(this));
  }

  handleArticleActionsLoaded() {
    if (this.isScrollPositionInsideArticle()) {
      this.articleActionsHiddenValue = false;
    }
  }

  handleScrollChange() {
    this.forceNavbarDefaultColorsValue = this.hasArticleHeaderTarget && this.articleHeaderOutOfView();

    if (this.isScrollPositionInsideArticle()) {
      const isScrollingDown = this.isScrollingDown();
      this.topbarHiddenValue = this.navbarPositionIsSticky && isScrollingDown;
      this.articleActionsHiddenValue = isScrollingDown;
    } else {
      this.topbarHiddenValue = false;
      this.articleActionsHiddenValue = true;
    }

    this.storePreviousScrollPosition();
    dispatchCustomEvent("article-reading-progress:update");
  }

  topbarHiddenValueChanged() {
    if(this.topbarHiddenValue) {
      this.hideTopbar();
    } else {
      this.showTopbar();
    }
  }

  forceNavbarDefaultColorsValueChanged() {
    document.documentElement.classList.toggle('navbar-force-default-colors', this.forceNavbarDefaultColorsValue)
  }

  articleActionsHiddenValueChanged() {
    if(this.articleActionsHiddenValue) {
      hideArticleActions();
    } else {
      showArticleActions();
    }
  }

  storePreviousScrollPosition() {
    this.previousScrollPositionValue = this.currentScrollPosition();
  }

  currentScrollPosition() {
    return Math.abs(document.body.getBoundingClientRect().top);
  }

  articleHeaderOutOfView() {
    const details = this.articleHeaderTarget.getBoundingClientRect();
    return Math.abs(details.top) > details.height;
  }

  isScrollPositionInsideArticle() {
    const contentPosFromTop = this.contentTarget.getBoundingClientRect().top;
    const contentHeight = this.contentTarget.clientHeight;
    const halfWindowHeight = window.innerHeight / 2;
    const contentScrolledHalfIntoWindow = contentPosFromTop < halfWindowHeight;
    const contentScrolledNotYetOutOfView = contentPosFromTop - halfWindowHeight + contentHeight > 0;
    const contentInView = contentScrolledHalfIntoWindow && contentScrolledNotYetOutOfView;

    return contentInView;
  }

  isScrollingDown() {
    return this.previousScrollPositionValue < this.currentScrollPosition();
  }

  hideTopbar() {
    if(this.hasTopbarTarget) {
      this.topbarTarget.classList.add('navbar-hidden');
    }
  }

  showTopbar() {
    if(this.hasTopbarTarget) {
      this.topbarTarget.classList.remove('navbar-hidden');
    }
  }
}
